import React, { useRef, useState, useEffect } from 'react';
import FormGroupDescription from '../../../../components/formGroupDescription';
import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiSpacer, EuiSuperSelect } from '@elastic/eui';
import { Controller } from 'react-hook-form';
import axios from 'axios';
import { useSelector } from 'react-redux';

const SplashScreen = ({ watch, control, setValue, defaultValue }) => {
    const selectedRestaurantId = useSelector((state) => state.auth.selectedRestaurantId);

    const splashTypeOptions = [
        { value: 'static', inputDisplay: 'Static Logo (Default)' },
        { value: 'image', inputDisplay: 'Image' },
        { value: 'animated', inputDisplay: 'Animated Logo' },
        { value: 'video', inputDisplay: 'Video' },
    ];

    // Simple icon components
    const CheckCircleIcon = ({ size = 20 }) => (
        <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
            <polyline points='22,4 12,14.01 9,11.01' />
        </svg>
    );

    const AlertCircleIcon = ({ size = 20 }) => (
        <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='12' cy='12' r='10' />
            <line x1='12' y1='8' x2='12' y2='12' />
            <line x1='12' y1='16' x2='12.01' y2='16' />
        </svg>
    );

    const PlayIcon = ({ size = 16 }) => (
        <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <polygon points='5,3 19,12 5,21' />
        </svg>
    );

    const PauseIcon = ({ size = 16 }) => (
        <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <rect x='6' y='4' width='4' height='16' />
            <rect x='14' y='4' width='4' height='16' />
        </svg>
    );

    const RotateCcwIcon = ({ size = 16 }) => (
        <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <polyline points='1,4 1,10 7,10' />
            <path d='M3.51 15a9 9 0 1 0 2.13-9.36L1 10' />
        </svg>
    );

    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isExistingFile, setIsExistingFile] = useState(false);
    const [urlError, setUrlError] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState(null);
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    // Enhanced function to construct and test file URLs
    const constructFileUrl = async (filePath) => {
        if (!filePath) return null;

        try {
            const response = await fetch(filePath, { method: 'HEAD' });
            if (response.ok) {
                return filePath;
            }
        } catch (error) {
            console.log(`error ==>`, error);
        }
    };

    // Initialize existing file data from defaultValue
    useEffect(() => {
        const initializeExistingFile = async () => {
            // First check defaultValue for existing splash screen data
            if (defaultValue?.splash_screen) {
                const splashScreenData = defaultValue.splash_screen;

                // Determine the splash type based on the type from data
                let splashTypeValue = 'static';

                if (splashScreenData.type === 'video') {
                    splashTypeValue = 'video';
                } else if (splashScreenData.type === 'image') {
                    splashTypeValue = 'image';
                } else if (splashScreenData.type === 'animated') {
                    splashTypeValue = 'animated';
                }

                // Set the form value for splash type
                if (setValue) {
                    setValue('splash_screen.type', splashTypeValue);
                }

                // If there's a URL and it's not static type, set preview
                if (splashScreenData.url && splashTypeValue !== 'static') {
                    try {
                        const fileUrl = splashScreenData.url;
                        setPreviewUrl(fileUrl);
                        setIsExistingFile(true);
                        setUrlError(false);

                        // Extract filename from URL or use a default
                        const filename = splashScreenData.url.split('/').pop() || 'existing-file';
                        setUploadedFileName(filename);
                    } catch (error) {
                        console.error('Error setting file URL:', error);
                        setUrlError(true);
                    }
                }
            } else {
                // Fallback to watching form values for existing data
                const splashScreenData = watch('splash_screen');

                if (
                    splashScreenData &&
                    (splashScreenData.file_path || splashScreenData.url) &&
                    splashScreenData.type !== 'static'
                ) {
                    try {
                        const filePath = splashScreenData.file_path || splashScreenData.url;
                        const fileUrl = await constructFileUrl(filePath);
                        setPreviewUrl(fileUrl);
                        setIsExistingFile(true);
                        setUrlError(false);
                        setUploadedFileName(splashScreenData.fileName || filePath.split('/').pop() || 'existing-file');
                    } catch (error) {
                        console.error('Error constructing file URL:', error);
                        setUrlError(true);
                    }
                }
            }
        };
        initializeExistingFile();
    }, [defaultValue, setValue, watch]);

    const clearFile = () => {
        setPreviewUrl(null);
        setUploadStatus(null);
        setIsPlaying(false);
        setIsExistingFile(false);
        setUrlError(false);
        setUploadedFileName(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const validateFile = (file) => {
        const errors = [];
        const splashType = watch('splash_screen.type');

        switch (splashType) {
            case 'image':
                const validImageTypes = ['image/gif', 'image/jpeg'];
                const validExtensions = ['.gif', '.jpg', '.jpeg'];
                if (
                    !validImageTypes.includes(file.type) &&
                    !validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
                ) {
                    errors.push('Please upload a valid image file (GIF or JPEG)');
                }
                if (file.size > 1 * 1024 * 1024) {
                    errors.push('Image file size must be less than 1MB');
                }
                break;

            case 'animated':
                if (file.type !== 'application/json' && !file.name.toLowerCase().endsWith('.json')) {
                    errors.push('Please upload a valid JSON file (Lottie animation)');
                }
                if (file.size > 1 * 1024 * 1024) {
                    errors.push('Animation file size must be less than 1MB');
                }
                break;

            case 'video':
                const validVideoTypes = ['video/mp4', 'video/quicktime'];
                const validVideoExtensions = ['.mp4', '.mov'];
                if (
                    !validVideoTypes.includes(file.type) &&
                    !validVideoExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
                ) {
                    errors.push('Please upload a valid video file (MP4 or MOV)');
                }
                if (file.size > 1 * 1024 * 1024) {
                    errors.push('Video file size must be less than 1MB');
                }
                break;

            default:
                break;
        }

        return errors;
    };

    const uploadMediaToAPI = async (file, splashType = null) => {
        const typeToUse = splashType || watch('splash_screen.type');

        let requestData;
        let headers = {};

        // Get auth token for the request
        const authToken = localStorage.getItem('authToken');
        const baseUrl = process.env.REACT_APP_BASE_URL_V5;

        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        }

        // Handle static type - send JSON with null media_type
        if (typeToUse === 'static') {
            requestData = {
                media_type: null,
            };
            headers['Content-Type'] = 'application/json';

            try {
                const response = await axios.post(
                    `${baseUrl}/restaurants/${selectedRestaurantId}/splash-screen/uploads`,
                    requestData,
                    { headers }
                );

                if (!response.data.success) {
                    throw new Error('Upload failed');
                }
                return response.data.data || response.data;
            } catch (error) {
                console.error('Upload error:', {
                    error: error.response?.data || error.message,
                    status: error.response?.status,
                });

                if (error.response?.data?.errors?.media) {
                    throw new Error(error.response.data.errors.media);
                }
                throw error;
            }
        } else {
            // Handle file uploads for other types
            const formData = new FormData();
            formData.append('media', file);

            let mediaType;
            if (typeToUse === 'video') {
                mediaType = 'video';
            } else if (typeToUse === 'animated' || file.type === 'application/json') {
                mediaType = 'animated';
            } else {
                mediaType = 'image';
            }
            formData.append('media_type', mediaType);

            try {
                // Don't set Content-Type for FormData, let axios handle it
                const response = await axios.post(
                    `${baseUrl}/restaurants/${selectedRestaurantId}/splash-screen/uploads`,
                    formData,
                    { headers }
                );

                if (!response.data.success) {
                    throw new Error('Upload failed');
                }
                return response.data.data || response.data;
            } catch (error) {
                console.error('Upload error:', {
                    error: error.response?.data || error.message,
                    status: error.response?.status,
                });

                if (error.response?.data?.errors?.media) {
                    throw new Error(error.response.data.errors.media);
                }
                throw error;
            }
        }
    };

    const handleFileUpload = async (file) => {
        const validationErrors = validateFile(file);

        if (validationErrors.length > 0) {
            setUploadStatus({
                type: 'error',
                message: validationErrors[0],
            });
            return;
        }

        // Immediately set the preview URL using object URL for instant feedback
        const localUrl = URL.createObjectURL(file);
        setPreviewUrl(localUrl);
        setUploadedFileName(file.name);
        setIsExistingFile(false);
        setUrlError(false);

        // Now upload in the background
        setIsUploading(true);
        setUploadStatus(null);

        try {
            const apiResponse = await uploadMediaToAPI(file);

            // If we get a file path from the API, update the preview URL
            const filePath = apiResponse?.file_path || apiResponse?.path || apiResponse?.url;

            if (filePath) {
                const fileUrl = await constructFileUrl(filePath);
                // Update to use the server URL instead of the local object URL
                setPreviewUrl(fileUrl);
            }
            // If no file path, we already have the object URL set

            setUploadStatus({
                type: 'success',
                message: 'File uploaded successfully!',
            });
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadStatus({
                type: 'error',
                message: error.message || 'Upload failed. Please try again.',
            });
            // Keep the local preview even if upload fails
        } finally {
            setIsUploading(false);
        }
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFileUpload(files[0]);
        }
    };

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            handleFileUpload(files[0]);
        }
    };

    const toggleVideoPlayback = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const restartVideo = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
            setIsPlaying(true);
        }
    };

    const getFileFormatText = () => {
        switch (watch('splash_screen.type')) {
            case 'image':
                return 'Supported formats: GIF or JPEG (Max size: 1MB)';
            case 'animated':
                return 'Supported formats: JSON (Lottie animation) (Max size: 1MB)';
            case 'video':
                return 'Supported formats: MP4, MOV (Max size: 1MB, Max duration: 3 seconds, Muted)';
            default:
                return '';
        }
    };

    const handleSplashTypeChange = async (value) => {
        // Clear file when type changes
        clearFile();

        // If static is selected, call the API with null media_type
        if (value === 'static') {
            setIsUploading(true);
            setUploadStatus(null);

            try {
                await uploadMediaToAPI(null, 'static');
                setUploadStatus({
                    type: 'success',
                    message: 'Static logo splash screen selected successfully!',
                });
            } catch (error) {
                console.error('Failed to set static splash screen:', error);
                setUploadStatus({
                    type: 'error',
                    message: error.message || 'Failed to set static splash screen. Please try again.',
                });
            } finally {
                setIsUploading(false);
            }
        }
    };
    return (
        <FormGroupDescription
            title='Splash Screen'
            description='Configure the splash screen that appears when users open your app'
        >
            <EuiFlexGroup>
                <EuiFlexItem>
                    <EuiFormRow label='Splash Screen Type'>
                        <Controller
                            control={control}
                            name='splash_screen.type'
                            render={({ field }) => (
                                <EuiSuperSelect
                                    options={splashTypeOptions}
                                    valueOfSelected={field.value}
                                    onChange={(value) => {
                                        field.onChange(value);
                                        handleSplashTypeChange(value);
                                    }}
                                />
                            )}
                        />
                    </EuiFormRow>
                </EuiFlexItem>
            </EuiFlexGroup>

            {/* Show upload status for static option */}
            {watch('splash_screen.type') === 'static' && uploadStatus && (
                <EuiFlexGroup style={{ marginTop: '16px' }}>
                    <EuiFlexItem>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px',
                                borderRadius: '6px',
                                backgroundColor: uploadStatus.type === 'success' ? '#E6F2F1' : '#F8E8E8',
                                border: `1px solid ${uploadStatus.type === 'success' ? '#B7D5D3' : '#E6B3B3'}`,
                                color: uploadStatus.type === 'success' ? '#017D73' : '#BD271E',
                            }}
                        >
                            {uploadStatus.type === 'success' ? (
                                <CheckCircleIcon size={20} />
                            ) : (
                                <AlertCircleIcon size={20} />
                            )}
                            <span>{uploadStatus.message}</span>
                        </div>
                    </EuiFlexItem>
                </EuiFlexGroup>
            )}

            {['image', 'animated', 'video'].includes(watch('splash_screen.type')) && (
                <>
                    <EuiSpacer size='m' />
                    <EuiFlexGroup>
                        <EuiFlexItem>
                            <EuiFormRow
                                label='Upload File'
                                style={{ marginTop: '16px', width: '100%', maxWidth: '500px' }}
                            >
                                <div
                                    style={{
                                        border: `2px dashed ${
                                            dragActive
                                                ? '#006BB4'
                                                : previewUrl || uploadedFileName
                                                  ? '#017D73'
                                                  : '#D3DAE6'
                                        }`,
                                        borderRadius: '8px',
                                        padding: '24px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        backgroundColor: dragActive
                                            ? '#E6F7FF'
                                            : previewUrl || uploadedFileName
                                              ? '#E6F2F1'
                                              : 'transparent',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={!(previewUrl || uploadedFileName) ? triggerFileInput : undefined}
                                >
                                    {previewUrl || uploadedFileName || isExistingFile ? (
                                        <div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    marginBottom: '12px',
                                                }}
                                            >
                                                <CheckCircleIcon size={32} />
                                                <span style={{ fontWeight: '600', color: '#017D73' }}>
                                                    {uploadedFileName ||
                                                        watch('splash_screen.fileName') ||
                                                        'Existing file'}
                                                    {isExistingFile && (
                                                        <span
                                                            style={{
                                                                fontSize: '12px',
                                                                color: '#69707D',
                                                                marginLeft: '8px',
                                                            }}
                                                        >
                                                            (Current file)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>

                                            {isExistingFile && urlError && (
                                                <div
                                                    style={{
                                                        marginBottom: '16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '12px',
                                                        borderRadius: '6px',
                                                        backgroundColor: '#FFF4E5',
                                                        color: '#C67E14',
                                                        border: '1px solid #FFD299',
                                                    }}
                                                >
                                                    <AlertCircleIcon size={20} />
                                                    <span>
                                                        Unable to preview existing file. The file may still work in the
                                                        app.
                                                    </span>
                                                </div>
                                            )}

                                            {previewUrl && !urlError && watch('splash_screen.type') === 'image' && (
                                                <div
                                                    style={{
                                                        marginTop: '16px',
                                                        width: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <div style={{ width: '100%', maxWidth: '352px', margin: '0 auto' }}>
                                                        <div
                                                            style={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                backgroundColor: 'white',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                            }}
                                                        >
                                                            <img
                                                                src={previewUrl}
                                                                alt='Uploaded preview'
                                                                style={{
                                                                    width: '100%',
                                                                    height: 'auto',
                                                                    maxHeight: '480px',
                                                                    objectFit: 'contain',
                                                                    display: 'block',
                                                                }}
                                                                onError={(e) => {
                                                                    console.error('Image loading error:', e);
                                                                    if (isExistingFile) {
                                                                        setUrlError(true);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {previewUrl && !urlError && watch('splash_screen.type') === 'video' && (
                                                <div
                                                    style={{
                                                        marginTop: '16px',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: '352px',
                                                            margin: '0 auto',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {/* Video Container */}
                                                        <div
                                                            style={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                height: '380px',
                                                                backgroundColor: 'black',
                                                                borderRadius: '8px',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <video
                                                                ref={videoRef}
                                                                src={previewUrl}
                                                                muted
                                                                controls
                                                                style={{
                                                                    display: 'block',
                                                                    maxWidth: '100%',
                                                                    maxHeight: '100%',
                                                                    objectFit: 'contain',
                                                                    margin: '0 auto',
                                                                }}
                                                                onPlay={() => setIsPlaying(true)}
                                                                onPause={() => setIsPlaying(false)}
                                                                onEnded={() => setIsPlaying(false)}
                                                                onError={(e) => {
                                                                    console.error('Video loading error:', e);
                                                                    if (isExistingFile) {
                                                                        setUrlError(true);
                                                                    }
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Controls */}
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                                marginTop: '8px',
                                                            }}
                                                        >
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleVideoPlayback();
                                                                }}
                                                                style={{
                                                                    backgroundColor: '#6C5DD3',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    padding: '8px 16px',
                                                                    borderRadius: '8px',
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer',
                                                                    transition: 'background-color 0.2s',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                }}
                                                            >
                                                                {isPlaying ? (
                                                                    <PauseIcon size={16} />
                                                                ) : (
                                                                    <PlayIcon size={16} />
                                                                )}
                                                                {isPlaying ? 'Pause' : 'Play'}
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    restartVideo();
                                                                }}
                                                                style={{
                                                                    backgroundColor: '#525252',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    padding: '8px 16px',
                                                                    borderRadius: '8px',
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer',
                                                                    transition: 'background-color 0.2s',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                }}
                                                                onMouseEnter={(e) =>
                                                                    (e.target.style.backgroundColor = '#404040')
                                                                }
                                                                onMouseLeave={(e) =>
                                                                    (e.target.style.backgroundColor = '#525252')
                                                                }
                                                            >
                                                                <RotateCcwIcon size={16} /> Restart
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {previewUrl && !urlError && watch('splash_screen.type') === 'animated' && (
                                                <div
                                                    style={{
                                                        marginTop: '16px',
                                                        width: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <div style={{ width: '100%', maxWidth: '352px', margin: '0 auto' }}>
                                                        <div
                                                            style={{
                                                                padding: '24px',
                                                                backgroundColor: '#4B39C9',
                                                                borderRadius: '12px',
                                                                color: 'white',
                                                                textAlign: 'center',
                                                                boxShadow: '0 4px 6px rgba(75, 57, 201, 0.2)',
                                                            }}
                                                        >
                                                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                                                Lottie Animation
                                                            </div>
                                                            <div
                                                                style={{
                                                                    marginTop: '8px',
                                                                    fontSize: '14px',
                                                                    opacity: 0.9,
                                                                }}
                                                            >
                                                                {isExistingFile
                                                                    ? 'Existing animation will be rendered in the app'
                                                                    : 'Animation will be rendered in the app'}
                                                            </div>
                                                            {(isExistingFile || uploadedFileName) && (
                                                                <div
                                                                    style={{
                                                                        fontSize: '12px',
                                                                        marginTop: '8px',
                                                                        opacity: 0.7,
                                                                    }}
                                                                >
                                                                    File:{' '}
                                                                    {uploadedFileName ||
                                                                        watch('splash_screen.fileName')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div style={{ marginTop: '16px' }}>
                                                <EuiButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        triggerFileInput();
                                                    }}
                                                    size='s'
                                                >
                                                    Replace File
                                                </EuiButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <div style={{ marginBottom: '16px' }}>
                                                <svg
                                                    width='48'
                                                    height='48'
                                                    viewBox='0 0 24 24'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='2'
                                                >
                                                    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                                                    <polyline points='7,10 12,15 17,10' />
                                                    <line x1='12' y1='15' x2='12' y2='3' />
                                                </svg>
                                            </div>
                                            <div style={{ marginBottom: '4px', color: '#343741' }}>
                                                {isUploading ? 'Uploading...' : 'Drop your file here'}
                                            </div>
                                            <div style={{ color: '#69707D', marginBottom: '12px' }}>
                                                or click to browse
                                            </div>
                                            <EuiButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    triggerFileInput();
                                                }}
                                                isLoading={isUploading}
                                                size='s'
                                            >
                                                Browse Files
                                            </EuiButton>
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type='file'
                                        style={{ display: 'none' }}
                                        accept={
                                            watch('splash_screen.type') === 'image'
                                                ? 'image/gif,image/jpeg'
                                                : watch('splash_screen.type') === 'animated'
                                                  ? '.json,application/json'
                                                  : 'video/mp4,video/quicktime'
                                        }
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </EuiFormRow>
                        </EuiFlexItem>
                    </EuiFlexGroup>

                    <div style={{ color: '#69707D', marginTop: '8px' }}>{getFileFormatText()}</div>

                    {uploadStatus && (
                        <EuiFlexGroup style={{ marginTop: '16px' }}>
                            <EuiFlexItem>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        backgroundColor: uploadStatus.type === 'success' ? '#E6F2F1' : '#F8E8E8',
                                        border: `1px solid ${uploadStatus.type === 'success' ? '#B7D5D3' : '#E6B3B3'}`,
                                        color: uploadStatus.type === 'success' ? '#017D73' : '#BD271E',
                                    }}
                                >
                                    {uploadStatus.type === 'success' ? (
                                        <CheckCircleIcon size={20} />
                                    ) : (
                                        <AlertCircleIcon size={20} />
                                    )}
                                    <span>{uploadStatus.message}</span>
                                </div>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    )}

                    <EuiSpacer size='m' />

                    {watch('splash_screen.type') === 'image' && (
                        <EuiFlexGroup>
                            <EuiFlexItem>
                                <div
                                    style={{
                                        backgroundColor: '#F5F7FA',
                                        padding: '16px',
                                        borderRadius: '6px',
                                        border: '1px solid #D3DAE6',
                                    }}
                                >
                                    <p style={{ fontWeight: '600', color: '#343741' }}>
                                        💡 Recommendation: Image Best Practices
                                    </p>
                                    <p style={{ marginTop: '8px', color: '#69707D' }}>
                                        For best results, use high-quality images with transparent backgrounds when
                                        needed. Recommended resolution is 2160×4096 for full-screen display. Maximum
                                        file size is 1MB.
                                    </p>
                                </div>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    )}

                    {watch('splash_screen.type') === 'animated' && (
                        <EuiFlexGroup>
                            <EuiFlexItem>
                                <div
                                    style={{
                                        backgroundColor: '#F5F7FA',
                                        padding: '16px',
                                        borderRadius: '6px',
                                        border: '1px solid #D3DAE6',
                                    }}
                                >
                                    <p style={{ fontWeight: '600', color: '#343741' }}>
                                        💡 Recommendation: Use Lottie Format
                                    </p>
                                    <p style={{ marginTop: '8px', color: '#69707D' }}>
                                        Lottie animations (.json) provide better performance, smaller file sizes, and
                                        higher quality compared to GIFs. They're perfect for splash screen animations
                                        and will give your users the best experience.
                                    </p>
                                </div>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    )}

                    {watch('splash_screen.type') === 'video' && (
                        <EuiFlexGroup>
                            <EuiFlexItem>
                                <div
                                    style={{
                                        backgroundColor: '#F5F7FA',
                                        padding: '16px',
                                        borderRadius: '6px',
                                        border: '1px solid #D3DAE6',
                                    }}
                                >
                                    <p style={{ fontWeight: '600', color: '#343741' }}>💡 Video Best Practices</p>
                                    <p style={{ marginTop: '8px', color: '#69707D' }}>
                                        For best results, use short video clips 3 seconds with simple animations. Keep
                                        file sizes small for faster loading. Recommended resolution is 1080p or lower.
                                        The video will automatically loop in the app.
                                    </p>
                                </div>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    )}
                </>
            )}
        </FormGroupDescription>
    );
};

export default SplashScreen;
